import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Activity, Ban, KeyRound, Lightbulb, MessageSquareWarning, Pencil, Plus, Trash2 } from 'lucide-react'
import { governanceApi } from '../../services/api'
import { dialog } from '../../services/dialog'
import { RuleForm, type RulePreset } from './RuleForm'
import { ActivityTable } from './ActivityTable'
import {
  ENFORCEMENT_MODE_OPTIONS,
  type GovernanceEnforcementMode,
  type GovernancePolicy,
  type GovernanceRule,
  type GovernanceRuleAction,
  type GovernanceRuleType,
} from './types'

const SECTIONS: { type: GovernanceRuleType; title: string; description: string; icon: ReactNode }[] = [
  {
    type: 'topic',
    title: 'Forbidden topics',
    description: 'AI screens messages against a plain-language description of what is not allowed.',
    icon: <MessageSquareWarning size={14} className="text-muted-foreground" />,
  },
  {
    type: 'keyword',
    title: 'Trigger words',
    description: 'Deterministic, case-insensitive match against a list of phrases.',
    icon: <KeyRound size={14} className="text-muted-foreground" />,
  },
  {
    type: 'barrier',
    title: 'Barriers',
    description: 'Block all communication between two people or departments — always Block.',
    icon: <Ban size={14} className="text-muted-foreground" />,
  },
]

/** One-click starting points requested by Sales (mirror the public-site
 *  examples). Clicking a card opens the Topics create form prefilled — the
 *  admin still reviews recipients/surfaces and saves explicitly. `badge` is
 *  the customer-facing outcome wording; the underlying action is what the
 *  engine enforces (block escalates to hold/hide by the policy's mode). */
const SUGGESTED_RULES: { badge: string; preset: RulePreset }[] = [
  {
    badge: 'Alert manager',
    preset: {
      name: 'Client contact details shared outside client channels',
      aiDescription: "Sharing a client's contact details (phone number, email address, home or delivery address) anywhere outside the dedicated client channels.",
      action: 'notify',
      surfaces: ['chat'],
    },
  },
  {
    badge: 'Hold for review',
    preset: {
      name: 'Files sent to personal email addresses',
      aiDescription: 'Sending or offering to send company files, documents, exports or reports to personal (non-corporate) email addresses such as gmail.com, yahoo.com or similar.',
      action: 'block',
      surfaces: ['chat', 'mail'],
    },
  },
  {
    badge: 'Block',
    preset: {
      name: 'Contractors messaging in client channels',
      aiDescription: 'Messages posted by contractors or external collaborators inside client-facing channels.',
      action: 'block',
      surfaces: ['chat'],
    },
  },
]

/** Server Admin -> Policies tab: server-wide (departmentId=null) layer of
 *  Communication Governance — enabled/mode toggle, the three rule-type
 *  sections, and the activity feed. Task 9 will add the analogous
 *  department-layer page reusing RuleForm + ActivityTable. */
export function PoliciesTab({ serverId }: { serverId: string }) {
  const [policy, setPolicy] = useState<GovernancePolicy | null>(null)
  const [rules, setRules] = useState<GovernanceRule[]>([])
  const [effectiveMode, setEffectiveMode] = useState<GovernanceEnforcementMode>('notify_only')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingPolicy, setSavingPolicy] = useState(false)

  // undefined = no panel open. A section's "Add rule" sets creatingType;
  // clicking Edit on a row sets editingRuleId. At most one panel is open
  // tab-wide so the page never shows two forms fighting for attention.
  const [creatingType, setCreatingType] = useState<GovernanceRuleType | null>(null)
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null)
  // Prefill for the create form when opened from a Suggested-rules card.
  const [presetDraft, setPresetDraft] = useState<RulePreset | null>(null)

  const load = useCallback(async () => {
    if (!serverId) return
    try {
      const res = await governanceApi.getServerPolicy(serverId)
      setPolicy(res.data.policy)
      setRules(res.data.rules ?? [])
      setEffectiveMode(res.data.effectiveMode)
      setError(null)
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load communication governance policy')
    } finally {
      setLoading(false)
    }
  }, [serverId])

  useEffect(() => { load() }, [load])

  // No policy row yet means the server layer has never been saved — show
  // what a PUT would create (mirrors getOrCreatePolicy's defaults in
  // routes/governance.ts) so the controls aren't blank before the first save.
  const enabled = policy?.enabled ?? true
  const enforcementMode = policy?.enforcementMode ?? 'post_delivery'

  const patchPolicy = async (data: { enabled?: boolean; enforcementMode?: GovernanceEnforcementMode }) => {
    setSavingPolicy(true)
    setError(null)
    try {
      await governanceApi.putServerPolicy(serverId, data)
      await load() // effectiveMode depends on every enabled layer, not just this one
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update policy')
    } finally {
      setSavingPolicy(false)
    }
  }

  const closeForms = () => { setCreatingType(null); setEditingRuleId(null); setPresetDraft(null) }
  const startCreate = (type: GovernanceRuleType) => { setEditingRuleId(null); setPresetDraft(null); setCreatingType(type) }
  const startEdit = (rule: GovernanceRule) => { setCreatingType(null); setPresetDraft(null); setEditingRuleId(rule.id) }
  const startPreset = (preset: RulePreset) => { setEditingRuleId(null); setPresetDraft(preset); setCreatingType('topic') }
  const handleSaved = async () => { closeForms(); await load() }

  const toggleRuleEnabled = async (rule: GovernanceRule) => {
    setError(null)
    try {
      await governanceApi.updateRule(serverId, rule.id, { enabled: !rule.enabled })
      await load()
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update rule')
    }
  }

  const handleDelete = async (rule: GovernanceRule) => {
    if (!(await dialog.confirm({ title: 'Delete rule', message: `Delete "${rule.name}"? This cannot be undone.`, tone: 'destructive', confirmLabel: 'Delete' }))) return
    setError(null)
    try {
      await governanceApi.deleteRule(serverId, rule.id)
      await load()
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to delete rule')
    }
  }

  if (loading) {
    return <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
  }

  return (
    <div className="space-y-4">
      {error && <div className="text-sm bg-destructive/10 text-destructive rounded px-3 py-2">{error}</div>}

      <div className="rounded-xl bg-card/40 ring-1 ring-border p-4 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-foreground">Communication governance</div>
            <div className="text-xs text-muted-foreground">AI-assisted screening of chat, voice, and mail against this server's policy.</div>
          </div>
          <Toggle checked={enabled} onChange={() => patchPolicy({ enabled: !enabled })} disabled={savingPolicy} label={enabled ? 'Enabled' : 'Disabled'} />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs font-medium text-muted-foreground shrink-0">Enforcement mode</label>
          <select
            value={enforcementMode}
            onChange={(e) => patchPolicy({ enforcementMode: e.target.value as GovernanceEnforcementMode })}
            disabled={savingPolicy || !enabled}
            className="policy-input flex-1 min-w-[280px]"
          >
            {ENFORCEMENT_MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {effectiveMode !== enforcementMode && (
          <div className="text-[11px] text-muted-foreground">
            Effective mode for this server (incl. department overrides): <span className="text-foreground font-medium">{effectiveMode}</span>
          </div>
        )}
        <div className="text-[11px] text-muted-foreground/70">
          Governed by the <code className="px-1 py-0.5 rounded bg-muted">COMMS_GOVERNANCE_ENABLED</code> environment kill-switch — when unset, screening is bypassed platform-wide regardless of this toggle.
        </div>
      </div>

      {(() => {
        const existingNames = new Set(rules.map((r) => r.name.toLowerCase()))
        const suggestions = SUGGESTED_RULES.filter((s) => !existingNames.has(s.preset.name!.toLowerCase()))
        if (suggestions.length === 0) return null
        return (
          <div className="rounded-xl ring-1 ring-border bg-card/40 overflow-hidden">
            <div className="px-4 py-2.5 bg-muted/40 border-b border-border flex items-center gap-2.5">
              <Lightbulb size={14} className="text-muted-foreground" />
              <div className="text-sm font-semibold text-foreground">Suggested rules</div>
              <span className="text-xs text-muted-foreground">Common starting points — click to prefill, review, save</span>
            </div>
            <div className="p-3 grid gap-2 sm:grid-cols-3">
              {suggestions.map((s) => (
                <button
                  key={s.preset.name}
                  type="button"
                  onClick={() => startPreset(s.preset)}
                  className="text-left rounded-lg ring-1 ring-border bg-card px-3 py-2.5 transition hover:ring-primary/50 hover:bg-accent/20"
                >
                  <div className="text-[13px] text-foreground leading-snug">{s.preset.name}</div>
                  <span className={`mt-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${actionChipClass(s.preset.action ?? 'notify')}`}>
                    {s.badge}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )
      })()}

      {SECTIONS.map((s) => (
        <RuleSection
          key={s.type}
          serverId={serverId}
          section={s}
          rules={rules.filter((r) => r.type === s.type)}
          creating={creatingType === s.type}
          preset={creatingType === s.type ? presetDraft ?? undefined : undefined}
          editingRuleId={editingRuleId}
          onStartCreate={() => startCreate(s.type)}
          onStartEdit={startEdit}
          onCancelForm={closeForms}
          onSaved={handleSaved}
          onToggleEnabled={toggleRuleEnabled}
          onDelete={handleDelete}
        />
      ))}

      <div className="rounded-xl ring-1 ring-border bg-card/40 overflow-hidden">
        <div className="px-4 py-2.5 bg-muted/40 border-b border-border flex items-center gap-2.5">
          <Activity size={14} className="text-muted-foreground" />
          <div className="text-sm font-semibold text-foreground">Activity</div>
          <span className="text-xs text-muted-foreground">Recent policy events for this server</span>
        </div>
        <div className="p-3">
          <ActivityTable serverId={serverId} />
        </div>
      </div>

      <style>{`.policy-input{background:var(--color-muted);color:var(--color-foreground);border-radius:6px;padding:6px 10px;font-size:13px;outline:none;border:1px solid transparent}.policy-input:focus{border-color:var(--color-primary)}.policy-input:disabled{opacity:0.6;cursor:not-allowed}`}</style>
    </div>
  )
}

function Toggle({ checked, onChange, disabled, label }: { checked: boolean; onChange: () => void; disabled?: boolean; label?: string }) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      {label && <span className="text-xs font-medium text-muted-foreground">{label}</span>}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={onChange}
        className={`h-5 w-9 rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed ${checked ? 'bg-primary' : 'bg-muted'}`}
      >
        <span className={`block h-4 w-4 rounded-full bg-white transition ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </button>
    </div>
  )
}

function actionChipClass(action: GovernanceRuleAction): string {
  return action === 'block'
    ? 'bg-destructive/10 text-destructive'
    : 'bg-amber-500/15 text-amber-500'
}

function RuleSection({
  serverId, section, rules, creating, preset, editingRuleId, onStartCreate, onStartEdit, onCancelForm, onSaved, onToggleEnabled, onDelete,
}: {
  serverId: string
  section: { type: GovernanceRuleType; title: string; description: string; icon: ReactNode }
  rules: GovernanceRule[]
  creating: boolean
  preset?: RulePreset
  editingRuleId: string | null
  onStartCreate: () => void
  onStartEdit: (rule: GovernanceRule) => void
  onCancelForm: () => void
  onSaved: () => void
  onToggleEnabled: (rule: GovernanceRule) => void
  onDelete: (rule: GovernanceRule) => void
}) {
  return (
    <div className="rounded-xl ring-1 ring-border bg-card/40 overflow-hidden">
      <div className="px-4 py-2.5 bg-muted/40 border-b border-border flex items-center gap-2.5">
        {section.icon}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground">
            {section.title} <span className="text-xs font-normal text-muted-foreground">({rules.length})</span>
          </div>
          <div className="text-[11px] text-muted-foreground">{section.description}</div>
        </div>
        <button
          onClick={onStartCreate}
          disabled={creating}
          className="shrink-0 px-3 py-1.5 text-xs font-semibold bg-primary text-primary-foreground rounded hover:bg-primary/90 transition disabled:opacity-50 flex items-center gap-1"
        >
          <Plus size={12} /> Add rule
        </button>
      </div>

      {creating && (
        <div className="p-4 border-b border-border bg-muted/20">
          <RuleForm key={preset?.name ?? 'blank'} serverId={serverId} departmentId={null} type={section.type} preset={preset} onSaved={onSaved} onCancel={onCancelForm} />
        </div>
      )}

      {rules.length === 0 && !creating ? (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">No {section.title.toLowerCase()} yet.</div>
      ) : (
        <div className="divide-y divide-border">
          {rules.map((r) =>
            editingRuleId === r.id ? (
              <div key={r.id} className="p-4 bg-muted/20">
                <RuleForm serverId={serverId} departmentId={null} type={section.type} initial={r} onSaved={onSaved} onCancel={onCancelForm} />
              </div>
            ) : (
              <RuleRow
                key={r.id}
                rule={r}
                onEdit={() => onStartEdit(r)}
                onToggleEnabled={() => onToggleEnabled(r)}
                onDelete={() => onDelete(r)}
              />
            ),
          )}
        </div>
      )}
    </div>
  )
}

function RuleRow({ rule, onEdit, onToggleEnabled, onDelete }: {
  rule: GovernanceRule
  onEdit: () => void
  onToggleEnabled: () => void
  onDelete: () => void
}) {
  const recipientCount = rule.recipientIds?.length ?? 0
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-accent/20 transition">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium truncate ${rule.enabled ? 'text-foreground' : 'text-muted-foreground line-through'}`}>{rule.name}</span>
          <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${actionChipClass(rule.action)}`}>
            {rule.action}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          {rule.surfaces.map((s) => (
            <span key={s} className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{s}</span>
          ))}
          {rule.type === 'barrier' && rule.barrierA && rule.barrierB && (
            <span className="text-[11px] text-muted-foreground font-mono">
              {rule.barrierA.kind}:{rule.barrierA.id.slice(0, 8)} ⇄ {rule.barrierB.kind}:{rule.barrierB.id.slice(0, 8)}
            </span>
          )}
          <span className="text-[11px] text-muted-foreground">
            {recipientCount > 0 ? `${recipientCount} recipient${recipientCount === 1 ? '' : 's'}` : 'Default recipients (dept head / server admins)'}
          </span>
        </div>
      </div>
      <Toggle checked={rule.enabled} onChange={onToggleEnabled} />
      <button onClick={onEdit} className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent/40 transition" title="Edit">
        <Pencil size={14} />
      </button>
      <button onClick={onDelete} className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition" title="Delete">
        <Trash2 size={14} />
      </button>
    </div>
  )
}
